/**
 * AudioInputButton Component
 * Button to open audio input device selector with gain control
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Mic, ChevronDown, Check, Volume2, Plus, Minus } from 'lucide-react';
import { Slider } from '../../ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../ui/dropdown-menu';
import { toast } from 'sonner';

export const AudioInputButton = ({
  selectedDevice,
  setSelectedDevice,
  inputGain = 2.0,
  setInputGain,
  onTestInput,
}) => {
  const [devices, setDevices] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);

  // Get available audio input devices
  const refreshDevices = async () => {
    try {
      // Request permission first (required to see device labels)
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setHasPermission(true);
      
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = deviceList.filter(d => d.kind === 'audioinput');
      setDevices(audioInputs);
      
      // Auto-select external device if available
      if (audioInputs.length > 0 && !selectedDevice) {
        const externalDevice = audioInputs.find(d => 
          !d.label.toLowerCase().includes('built-in') && 
          !d.label.toLowerCase().includes('default') &&
          d.label !== ''
        );
        if (externalDevice) {
          setSelectedDevice(externalDevice.deviceId);
          toast.success(`Found: ${externalDevice.label}`);
        } else {
          setSelectedDevice(audioInputs[0].deviceId);
        }
      }
    } catch (error) {
      console.error('Failed to get audio devices:', error);
      toast.error('Please allow microphone access');
    }
  };

  useEffect(() => {
    if (isOpen) {
      refreshDevices();
    }
  }, [isOpen]);

  const selectedDeviceLabel = devices.find(d => d.deviceId === selectedDevice)?.label || 'Select Input';

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative p-2 text-zinc-400 hover:text-white gap-1"
          data-testid="audio-input-button"
          title="Select Audio Input Device"
        >
          <Mic className="w-5 h-5" />
          <ChevronDown className="w-3 h-3" />
          {selectedDevice && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-emerald-500 rounded-full" />
          )}
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="start" className="w-72 bg-zinc-900 border-zinc-700">
        <DropdownMenuLabel className="text-zinc-400 text-xs">
          Audio Input Device
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-700" />
        
        {!hasPermission ? (
          <DropdownMenuItem
            onClick={refreshDevices}
            className="text-amber-400 cursor-pointer"
          >
            <Mic className="w-4 h-4 mr-2" />
            Click to allow microphone access
          </DropdownMenuItem>
        ) : devices.length === 0 ? (
          <DropdownMenuItem disabled className="text-zinc-500">
            No audio input devices found
          </DropdownMenuItem>
        ) : (
          devices.map((device) => (
            <DropdownMenuItem
              key={device.deviceId}
              onClick={() => {
                setSelectedDevice(device.deviceId);
                toast.success(`Selected: ${device.label || 'Audio Input'}`);
              }}
              className={`cursor-pointer flex items-center gap-2 ${
                selectedDevice === device.deviceId ? 'bg-violet-500/20' : ''
              }`}
            >
              {selectedDevice === device.deviceId && (
                <Check className="w-4 h-4 text-emerald-400" />
              )}
              <span className={`text-sm ${selectedDevice === device.deviceId ? 'text-white' : 'text-zinc-300'}`}>
                {device.label || `Input ${device.deviceId.slice(0, 8)}`}
              </span>
            </DropdownMenuItem>
          ))
        )}
        
        <DropdownMenuSeparator className="bg-zinc-700" />
        
        {/* Input Gain Control */}
        <div className="px-3 py-2">
          <DropdownMenuLabel className="text-zinc-400 text-xs px-0 mb-2">
            Input Gain: {inputGain.toFixed(1)}x {inputGain >= 4 && '🔊'}
          </DropdownMenuLabel>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="p-1 h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                setInputGain?.(Math.max(1, inputGain - 1));
              }}
            >
              <Minus className="w-3 h-3" />
            </Button>
            <Slider
              value={[inputGain]}
              onValueChange={([v]) => setInputGain?.(v)}
              min={1}
              max={10}
              step={0.5}
              className="flex-1"
            />
            <Button
              variant="ghost"
              size="sm"
              className="p-1 h-6 w-6"
              onClick={(e) => {
                e.stopPropagation();
                setInputGain?.(Math.min(10, inputGain + 1));
              }}
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
          <p className="text-[10px] text-zinc-500 mt-1">
            Boost for JamLab/iRig (try 5-8x)
          </p>
        </div>
        
        <DropdownMenuSeparator className="bg-zinc-700" />
        <DropdownMenuItem
          onClick={refreshDevices}
          className="text-zinc-400 cursor-pointer"
        >
          <Volume2 className="w-4 h-4 mr-2" />
          Refresh Devices
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AudioInputButton;
